import { Box, Stack, Typography } from '@mui/material'
import { BulkactionV1BulkActionServiceCreateBulkAction, TaskV1TaskServiceCreateRevokeTask } from 'c1gen/permissions'
import { AppV1AppUserServiceowner } from 'c1gen/roles'
import { C1IconButton, C1Tooltip } from 'c1ui'
import { BulkActionDropdown, BulkActionOption } from 'components/core/bulk-actions/BulkActionDropdown'
import { BulkCreateRevokeTasksDialog } from 'components/core/bulk-actions/BulkCreateRevokeTasksDialog'
import { C1DrawerInner } from 'components/core/drawer/C1DrawerInner'
import { DynamicHeightSizer } from 'components/core/dynamic-height/DynamicHeightSizer'
import { EmptyList } from 'components/core/empty-list'
import { C1Icon } from 'components/core/icons/C1Icon'
import List from 'components/core/list'
import { C1TableRedux, C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import { useHasPermission } from 'components/no-ui/capability'
import { C1EntitlementDisplayName } from 'components/pages/entitlement/display-name'
import { RevokeOrTaskButton } from 'components/pages/entitlement/users/list'
import { RevokeGrantRequestDialog } from 'components/pages/entitlement/users/list/revokeGrantRequestDialog'
import { ExportData } from 'components/pages/explore/query/ExportData'
import { RevokeTaskType } from 'components/pages/task/type'
import { useDrawer } from 'hooks/useDrawer'
import { AppEntitlementValueWithRevokable } from 'lib/actions/apps/app-entitlement'
import { AppUserValue } from 'lib/actions/apps/app-users'
import { TaskValue } from 'lib/actions/task'
import { useSimpleModal } from 'lib/modals'
import { ClipboardCheck } from 'lucide-react'
import { SearchAppEntitlementsWithRevokableForAppUserRequest } from 'pbts/c1/api/app/v1/app_entitlement'
import { AppEntitlementUserBindingRef } from 'pbts/c1/api/app/v1/app_refs'
import { ExportDataRequest, QueryId } from 'pbts/c1/api/exportdata/v1/exportdata'
import { TaskSearchServiceSearchTasksByGrantsRequest } from 'pbts/c1/api/task/v1/search'
import { FC, useCallback, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { searchAppEntitlementsWithRevokableForAppUserApi } from 'redux_toolkit/entitlements'
import { tasksSearchByGrantsApi } from 'redux_toolkit/tasks'

interface BaseProps {
    appUser: AppUserValue
}

type AppUserManageGrantsButtonProps =
    | (BaseProps & { revokeVisibleOnly: true; userId: string })
    | (BaseProps & { revokeVisibleOnly?: false; userId?: undefined })

export const AppUserManageGrantsButton: FC<AppUserManageGrantsButtonProps> =
    ({ appUser, userId, revokeVisibleOnly = false }): React.ReactNode => {
        const intl = useIntl()
        const [showDrawer] = useDrawer()

        const showGrantsDrawer = useCallback(() => {
            showDrawer(<AppUserManageGrantsDrawer appUser={appUser} userId={userId} revokeVisibleOnly={revokeVisibleOnly} />)
        }, [appUser, showDrawer, userId, revokeVisibleOnly])

        return <C1Tooltip title={intl.formatMessage({ id: 'component_app_users_option_view_access', defaultMessage: 'View access' })}>
            <Box>
                <C1IconButton
                    onClick={showGrantsDrawer}
                    variant="outlined"
                    color="info"
                    aria-label={intl.formatMessage({ id: 'component_app_users_option_view_access', defaultMessage: 'View access' })}
                >
                    <C1Icon icon={ClipboardCheck} />
                </C1IconButton>
            </Box>
        </C1Tooltip>
    }

type ExtraProps = {
    openRevokeDialog: (appEntitlement: AppEntitlementWithTaskAndRevokable) => void
    revokeVisibleOnly: boolean
}

export type AppEntitlementWithTaskAndRevokable = AppEntitlementValueWithRevokable & {
    task?: TaskValue
}

type AppUserManageGrantsDrawerProps = {
    appUser: AppUserValue
    userId?: string
    revokeVisibleOnly: boolean
}

const AppUserManageGrantsDrawer: FC<AppUserManageGrantsDrawerProps> = ({ appUser, userId, revokeVisibleOnly }) => {
    const canCreateBulkAction = useHasPermission(BulkactionV1BulkActionServiceCreateBulkAction)
    const [selections, setSelections] = useState<AppEntitlementUserBindingRef[]>([])
    const { open } = useSimpleModal()

    const cols = useMemo<C1TableReduxColumn<AppEntitlementWithTaskAndRevokable, ExtraProps>[]>(() => [
        {
            field: 'entitlement',
            label: <FormattedMessage id="app_user_manage_grants_entitlement" defaultMessage="Entitlement" />,
            Component: ({ row: entitlement }) => (
                <C1EntitlementDisplayName excludeDetails entitlement={entitlement} textTruncationWidth="400px" />
            ),
        },
        {
            field: 'actions',
            isSticky: () => true,
            shouldRender: (_, hasPermissions, hasRoles) => (hasPermissions(TaskV1TaskServiceCreateRevokeTask) ||
                hasRoles(AppV1AppUserServiceowner) || false),
            Component: ({ row: appEnt, extras }): React.ReactNode => (
                <RevokeOrTaskButton
                    disabled={!appEnt.isRevokable && extras.revokeVisibleOnly}
                    appEntitlementWithTask={appEnt}
                    onClick={(): void => extras.openRevokeDialog(appEnt)}
                />
            ),
        },
    ], [])

    const openRevokeDialog = useCallback((aeu: AppEntitlementWithTaskAndRevokable): void => {
        open(RevokeGrantRequestDialog, {
            appUser,
            appEntitlement: aeu,
        })()
    }, [appUser, open])

    const options = useMemo<BulkActionOption<AppEntitlementUserBindingRef, object>[]>(() =>
        [{
            key: 'createRevokeTask',
            label: <FormattedMessage id="bulk_action_create_revoke_task" defaultMessage="Revoke" />,
            behavior: {
                type: 'dialog' as const,
                Dialog: BulkCreateRevokeTasksDialog<AppEntitlementUserBindingRef>(),
            },
        }], [])

    const { data: entitlementData, isError, isLoading, error, canLoadMore, shouldInvalidateList, getPage } =
        searchAppEntitlementsWithRevokableForAppUserApi
            .useSearchAppEntitlementsWithRevokableForAppUser(SearchAppEntitlementsWithRevokableForAppUserRequest.create({
                appUserId: appUser.id,
                appId: appUser.appId,
                userId: userId ?? '',
                expandMask: { paths: ['*'] },
            }), {
                actionable: !!appUser.id && !!appUser.appId,
                notify: true,
                autoReload: true,
            })

    const [page, setPage] = useState<number>(0)
    const onPageChange = useCallback(
        async (n: number) => {
            await getPage(n)
            setPage(n)
        },
        [getPage],
    )

    const {
        data: taskData,
    } = tasksSearchByGrantsApi.useSearchTasksByGrants(TaskSearchServiceSearchTasksByGrantsRequest.create({
        taskType: RevokeTaskType,
        appEntitlementUserBindingRefs: entitlementData && entitlementData[page] ?
            entitlementData[page].map((v) => AppEntitlementUserBindingRef.create({
                appId: v.appId,
                appEntitlementId: v.id,
                appUserId: appUser.id,
            })) : [],
    }), {
        notify: true,
        autoReload: true,
        actionable: entitlementData[page] && entitlementData[page].length > 0,
        pageSize: entitlementData[page]?.length || 0,
    })

    const entitlementTaskMap = useMemo(() => (taskData ?? []).flat().reduce((acc, v) => {
        if (v.type?.taskType.oneofKind !== 'revoke') return acc
        const appEntitlementId = v.type.taskType.revoke.appEntitlementId

        acc.set(appEntitlementId, v)
        return acc
    }, new Map<string, TaskValue>()), [taskData])

    const entitlementDataWithTask = useMemo(() => (entitlementData ?? [])?.map((p, i) => (i === page
        ? p.map((v) => {
            const task = entitlementTaskMap?.get(v.id)
            return task ? { ...v, task } : v
        })
        : p
    )), [entitlementData, entitlementTaskMap, page])

    const exportDataRequest = useMemo(
        () => ExportDataRequest.create({
            queryId: QueryId.APP_USER_GRANTS,
            request: {
                oneofKind: 'searchAppEntitlementsWithRevokableForAppUserRequest',
                searchAppEntitlementsWithRevokableForAppUserRequest: SearchAppEntitlementsWithRevokableForAppUserRequest.create({
                    appUserId: appUser.id,
                    appId: appUser.appId,
                    userId: userId ?? '',
                    expandMask: { paths: ['*'] },
                }),
            },
            displayName: `App user access - ${appUser.displayName}`,
        }),
        [appUser, userId],
    )

    const onReload = searchAppEntitlementsWithRevokableForAppUserApi.useInvalidateSearchAppEntitlementsWithRevokableForAppUser()
    const table = useMemo(() => <C1TableRedux<AppEntitlementWithTaskAndRevokable, ExtraProps>
        cols={cols}
        data={entitlementDataWithTask}
        isError={isError}
        isLoading={isLoading}
        error={error}
        canLoadMore={canLoadMore}
        shouldReload={shouldInvalidateList}
        onReload={onReload}
        onPageChange={onPageChange}
        page={page}
        emptyState={<EmptyList title={<FormattedMessage
            id="components_user_list_no_grants_found"
            defaultMessage="This account doesn't have any access."
        />}
        />}
        getId={(row): string => row.id}
        staticExtraProps={{
            openRevokeDialog,
            revokeVisibleOnly,
        }}
        extraFooterActions={canCreateBulkAction
            ? <BulkActionDropdown<AppEntitlementUserBindingRef, object>
                selections={selections}
                setSelections={setSelections}
                options={options}
            />
            : undefined
        }
        setSelections={(_selections: AppEntitlementWithTaskAndRevokable[]): void => setSelections(_selections.map((selection) => ({
            appId: selection.appId,
            appEntitlementId: selection.id,
            appUserId: appUser.id,
        })))}
        disabledSelections={revokeVisibleOnly ? entitlementData?.flat()?.filter((entitlement) =>
            !entitlement.isRevokable) || [] : []}
        selections={selections.map((selection) =>
            entitlementData?.flat()?.find((entitlement) =>
                entitlement.appId === selection.appId &&
                entitlement.id === selection.appEntitlementId) ?? null).filter(Boolean) as AppEntitlementWithTaskAndRevokable[]}
    />, [entitlementData, isError, isLoading, error, canLoadMore, shouldInvalidateList,
        onReload, openRevokeDialog, canCreateBulkAction, cols, selections, options, onPageChange,
        page, appUser.id, entitlementDataWithTask, revokeVisibleOnly])

    return <C1DrawerInner>
        <C1DrawerInner.Header>
            <Stack spacing={2}>
                <Typography variant="h3" component="div">
                    <FormattedMessage
                        id="drawer_title_manage_access"
                        defaultMessage="{app} access for {app_user_name}"
                        values={{ app: appUser.app?.displayName, app_user_name: appUser.displayName }}
                    />
                </Typography>
                {appUser.email && <Typography>
                    {appUser.email}
                </Typography>}
            </Stack>
        </C1DrawerInner.Header>
        <C1DrawerInner.Content>
            <DynamicHeightSizer>
                <List<AppEntitlementWithTaskAndRevokable> table={table} buttons={<ExportData exportDataRequest={exportDataRequest} />} />
            </DynamicHeightSizer>
        </C1DrawerInner.Content>
    </C1DrawerInner>
}
